import { IPersistor } from './IPersistor';

export class IndexedDBPersistor implements IPersistor {
  private dbName: string;
  private storeName = 'keyvalue';
  private dbPromise: Promise<IDBDatabase> | null = null;
  private useSessionStorageFallback = false;

  constructor(dbName: string = 'app-persistence') {
    this.dbName = dbName;
    this.initDB();
  }

  private initDB(): void {
    if (!this.isIndexedDBAvailable()) {
      console.warn('[IndexedDBPersistor] IndexedDB not available, using SessionStorage fallback');
      this.useSessionStorageFallback = true;
      return;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('[IndexedDBPersistor] Failed to open database', request.error);
        this.useSessionStorageFallback = true;
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    }).catch((error) => {
      console.error('[IndexedDBPersistor] Database initialization failed', error);
      this.useSessionStorageFallback = true;
      return null;
    }) as Promise<IDBDatabase>;
  }

  private isIndexedDBAvailable(): boolean {
    try {
      return typeof indexedDB !== 'undefined';
    } catch {
      return false;
    }
  }

  private async getDB(): Promise<IDBDatabase | null> {
    if (this.useSessionStorageFallback) {
      return null;
    }

    try {
      return await this.dbPromise;
    } catch {
      this.useSessionStorageFallback = true;
      return null;
    }
  }

  async get<T>(key: string): Promise<T | null> {
    if (this.useSessionStorageFallback) {
      return this.sessionStorageGet<T>(key);
    }

    const db = await this.getDB();
    if (!db) {
      return this.sessionStorageGet<T>(key);
    }

    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result !== undefined ? request.result : null);
      };

      request.onerror = () => {
        console.error('[IndexedDBPersistor] Get failed', request.error);
        resolve(this.sessionStorageGet<T>(key));
      };
    });
  }

  async set<T>(key: string, value: T): Promise<void> {
    if (this.useSessionStorageFallback) {
      this.sessionStorageSet(key, value);
      return;
    }

    const db = await this.getDB();
    if (!db) {
      this.sessionStorageSet(key, value);
      return;
    }

    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(value, key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('[IndexedDBPersistor] Set failed', request.error);
        this.sessionStorageSet(key, value);
        resolve();
      };
    });
  }

  async delete(key: string): Promise<void> {
    if (this.useSessionStorageFallback) {
      sessionStorage.removeItem(key);
      return;
    }

    const db = await this.getDB();
    if (!db) {
      sessionStorage.removeItem(key);
      return;
    }

    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('[IndexedDBPersistor] Delete failed', request.error);
        sessionStorage.removeItem(key);
        resolve();
      };
    });
  }

  async clear(): Promise<void> {
    if (this.useSessionStorageFallback) {
      sessionStorage.clear();
      return;
    }

    const db = await this.getDB();
    if (!db) {
      sessionStorage.clear();
      return;
    }

    return new Promise((resolve) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('[IndexedDBPersistor] Clear failed', request.error);
        sessionStorage.clear();
        resolve();
      };
    });
  }

  private sessionStorageGet<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('[IndexedDBPersistor] SessionStorage get failed', error);
      return null;
    }
  }

  private sessionStorageSet<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('[IndexedDBPersistor] SessionStorage set failed', error);
    }
  }
}
